import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { supabase } from "../../supabaseClient";
import ShieldImg from "../../Assets/Icons/shield.png";
import FucaScoreLogo from "../../Assets/Images/fucascore_png.png";

const GROUP_LETTERS = ["A", "B", "C", "D", "E", "F", "G", "H"];

const AdminTournamentTVBroadcast = ({ turnir, onClose, onFinish }) => {
  const [step, setStep] = useState(1);

  const [category, setCategory] = useState("seniori");
  const [groupCount, setGroupCount] = useState(4);

  const [rawTeamsText, setRawTeamsText] = useState("");
  const [parsedTeams, setParsedTeams] = useState([]);

  const [drawPool, setDrawPool] = useState([]);
  const [assignedGroups, setAssignedGroups] = useState({});

  const [currentDrawnTeam, setCurrentDrawnTeam] = useState(null);
  const [animationTeam, setAnimationTeam] = useState(null);

  const [isAnimating, setIsAnimating] = useState(false);
  const [activeGroup, setActiveGroup] = useState(null);
  const [lastAssignedTeamId, setLastAssignedTeamId] = useState(null);

  const [drawNumber, setDrawNumber] = useState(0);

  const [isFullscreen, setIsFullscreen] = useState(false);
  const [completedCategories, setCompletedCategories] = useState({
    seniori: false,
    veterani: false,
  });
  const animationTimeoutRef = useRef(null);
  const finishTimeoutRef = useRef(null);

  useEffect(() => {
    const checkCompletedCategories = async () => {
      if (!turnir?.id) return;
      try {
        const { count: seniorCount } = await supabase
          .from("turniri_timovi")
          .select("*", { count: "exact", head: true })
          .eq("turnir_id", turnir.id);

        const { count: veteranCount } = await supabase
          .from("turniri_veterani_timovi")
          .select("*", { count: "exact", head: true })
          .eq("turnir_id", turnir.id);

        setCompletedCategories({
          seniori: (seniorCount || 0) > 0,
          veterani: (veteranCount || 0) > 0,
        });
      } catch (err) {
        console.error("Greška provjere kategorija:", err);
      }
    };

    checkCompletedCategories();
  }, [turnir?.id]);

  useEffect(() => {
    if (!turnir?.id) return;
    const storageKey = `fucascore_draw_${turnir.id}_${category}`;
    const savedState = localStorage.getItem(storageKey);

    if (savedState) {
      try {
        const parsed = JSON.parse(savedState);
        if (parsed && parsed.step > 1 && parsed.step < 5) {
          if (
            window.confirm(
              "Pronađen je nedovršeni ždrijeb u memoriji. Želite li nastaviti tamo gdje ste stali?",
            )
          ) {
            setStep(parsed.step);
            setGroupCount(parsed.groupCount || 4);
            setParsedTeams(parsed.parsedTeams || []);
            setDrawPool(parsed.drawPool || []);
            setAssignedGroups(parsed.assignedGroups || {});
            setDrawNumber(parsed.drawNumber || 0);
          } else {
            localStorage.removeItem(storageKey);
          }
        }
      } catch (e) {
        console.error("Greška čitanja localStorage:", e);
      }
    }
  }, [turnir?.id, category]);

  const areVeteransEnabled = useMemo(() => {
    if (!turnir?.features) return false;
    try {
      const parsedFeatures =
        typeof turnir.features === "string"
          ? JSON.parse(turnir.features)
          : turnir.features;
      return Boolean(parsedFeatures?.veterani);
    } catch (e) {
      console.error("Greška kod parsiranja features:", e);
      return false;
    }
  }, [turnir?.features]);

  // SPREMANJE U LOCALSTORAGE NAKON SVAKE PROMJENE KORAKA/ŽDRIJEBA
  useEffect(() => {
    if (!turnir?.id || step <= 1 || step >= 5) return;
    const storageKey = `fucascore_draw_${turnir.id}_${category}`;
    const currentState = {
      step,
      groupCount,
      parsedTeams,
      drawPool,
      assignedGroups,
      drawNumber,
    };
    localStorage.setItem(storageKey, JSON.stringify(currentState));
  }, [
    step,
    groupCount,
    parsedTeams,
    drawPool,
    assignedGroups,
    drawNumber,
    turnir?.id,
    category,
  ]);

  // ============================================================
  // 100VH / NO SCROLL
  // ============================================================

  useEffect(() => {
    const originalBodyOverflow = document.body.style.overflow;
    const originalHtmlOverflow = document.documentElement.style.overflow;

    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = originalBodyOverflow;
      document.documentElement.style.overflow = originalHtmlOverflow;
    };
  }, []);

  // ============================================================
  // CLEANUP
  // ============================================================

  useEffect(() => {
    const currentFinishTimeout = finishTimeoutRef.current;
    const currentAnimationTimeout = animationTimeoutRef.current;

    return () => {
      if (currentAnimationTimeout) {
        clearTimeout(currentAnimationTimeout);
      }

      if (currentFinishTimeout) {
        clearTimeout(currentFinishTimeout);
      }
    };
  }, []);

  // ============================================================
  // FULLSCREEN
  // ============================================================

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(Boolean(document.fullscreenElement));
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);

    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
    };
  }, []);

  const toggleFullscreen = async () => {
    try {
      if (!document.fullscreenElement) {
        await document.documentElement.requestFullscreen();
      } else {
        await document.exitFullscreen();
      }
    } catch (error) {
      console.error("Fullscreen error:", error);
    }
  };

  /* SAVE TO SUPABASE */

  const handleSaveToDatabase = async () => {
    try {
      // Odabir prave tablice na temelju odabrane kategorije (seniori ili veterani)
      const tableName =
        category === "veterani" ? "turniri_veterani_timovi" : "turniri_timovi";

      const teamsToInsert = [];

      // Prolazimo kroz sve grupe (npr. A, B, C, D) i njihove ekipe
      Object.entries(assignedGroups).forEach(([groupName, teams]) => {
        teams.forEach((team) => {
          teamsToInsert.push({
            turnir_id: turnir.id,
            name: team.name,
            group_name: groupName,
            logo_url: team.logo_url || null,
            players: [],
            points: 0,
            played: 0,
            won: 0,
            drawn: 0,
            lost: 0,
            goals_for: 0,
            goals_against: 0,
          });
        });
      });

      if (teamsToInsert.length === 0) {
        alert("Nema izvučenih ekipa za spremanje!");
        return;
      }

      // Spremanje u Supabase
      const { error } = await supabase.from(tableName).insert(teamsToInsert);

      if (error) {
        console.error("Greška pri spremanju u bazu:", error);
        alert("Došlo je do greške prilikom spremanja ekipa.");
        return;
      }
      localStorage.removeItem(`fucascore_draw_${turnir.id}_${category}`);

      setStep(5);
    } catch (err) {
      console.error("Neočekivana greška:", err);
    }
  };

  // ============================================================
  // DERIVED DATA
  // ============================================================

  const totalTeams = parsedTeams.length;

  const drawnTeams = Object.values(assignedGroups).reduce(
    (total, teams) => total + teams.length,
    0,
  );

  const groupsLetter = useMemo(
    () => GROUP_LETTERS.slice(0, groupCount),
    [groupCount],
  );

  const drawProgress = totalTeams > 0 ? (drawnTeams / totalTeams) * 100 : 0;

  // Dinamički izračun maksimalnog broja slotova po grupi
  const maxGroupSlots = useMemo(() => {
    if (totalTeams === 0 || groupCount === 0) return 4;
    return Math.ceil(totalTeams / groupCount);
  }, [totalTeams, groupCount]);

  // ============================================================
  // PROCESS TEAMS
  // ============================================================

  const handleProcessTeamsText = () => {
    const lines = rawTeamsText
      .split("\n")
      .map((line) => line.trim())
      .filter(Boolean);

    const cleaned = lines
      .map((line, index) => {
        const nameWithoutNumber = line.replace(/^\s*\d+[.)]\s*/, "").trim();

        return {
          id: index + 1,
          name: nameWithoutNumber,
          logo_url: null,
        };
      })
      .filter((team) => team.name.length > 0);

    if (cleaned.length === 0) {
      alert("Unesite barem jednu ekipu!");
      return;
    }

    if (cleaned.length < groupCount) {
      alert(`Za ${groupCount} grupa trebate minimalno ${groupCount} ekipa.`);
      return;
    }

    setParsedTeams(cleaned);
    setStep(3);
  };

  // ============================================================
  // START DRAW
  // ============================================================

  const startTheDraw = () => {
    const initialGroups = {};

    groupsLetter.forEach((group) => {
      initialGroups[group] = [];
    });

    setAssignedGroups(initialGroups);
    setDrawPool([...parsedTeams]);

    setCurrentDrawnTeam(null);
    setAnimationTeam(null);

    setActiveGroup(null);
    setLastAssignedTeamId(null);

    setDrawNumber(0);

    setStep(4);
  };

  // ============================================================
  // FIND GROUP
  // ============================================================

  const findTargetGroup = (groups) => {
    const keys = Object.keys(groups);

    if (!keys.length) return null;

    let targetGroup = keys[0];

    keys.forEach((group) => {
      if (groups[group].length < groups[targetGroup].length) {
        targetGroup = group;
      }
    });

    return targetGroup;
  };

  // ============================================================
  // FINALIZE DRAW
  // ============================================================

  const finalizeSingleDraw = useCallback(() => {
    setDrawPool((currentPool) => {
      if (!currentPool.length) {
        setIsAnimating(false);
        return currentPool;
      }

      const randomIndex = Math.floor(Math.random() * currentPool.length);

      const selectedTeam = currentPool[randomIndex];

      const updatedPool = currentPool.filter(
        (_, index) => index !== randomIndex,
      );

      setAssignedGroups((currentGroups) => {
        const targetGroup = findTargetGroup(currentGroups);

        if (!targetGroup) {
          return currentGroups;
        }

        const updatedGroups = {
          ...currentGroups,
          [targetGroup]: [...currentGroups[targetGroup], selectedTeam],
        };

        setCurrentDrawnTeam(selectedTeam);
        setAnimationTeam(null);

        setActiveGroup(targetGroup);
        setLastAssignedTeamId(selectedTeam.id);

        setDrawNumber((prev) => prev + 1);
        setIsAnimating(false);

        // Uklonjen automatski setTimeout koji je automatski prebacivao na step 5[cite: 12]

        return updatedGroups;
      });

      return updatedPool;
    });
  }, []);

  // ============================================================
  // DRAW NEXT
  // ============================================================

  const handleDrawNextTeam = () => {
    if (isAnimating || drawPool.length === 0) {
      return;
    }

    setIsAnimating(true);

    setCurrentDrawnTeam(null);
    setAnimationTeam(null);
    setActiveGroup(null);
    setLastAssignedTeamId(null);

    if (animationTimeoutRef.current) {
      clearTimeout(animationTimeoutRef.current);
    }

    let counter = 0;

    const totalTicks = 38;

    const tick = () => {
      if (!drawPool.length) {
        return;
      }

      const randomIndex = Math.floor(Math.random() * drawPool.length);

      setAnimationTeam(drawPool[randomIndex]);

      counter++;

      const progress = counter / totalTicks;

      let delay;

      if (progress < 0.45) {
        delay = 35;
      } else if (progress < 0.75) {
        delay = 65;
      } else {
        delay = 105;
      }

      if (counter >= totalTicks) {
        animationTimeoutRef.current = setTimeout(() => {
          finalizeSingleDraw();
        }, 550);

        return;
      }

      animationTimeoutRef.current = setTimeout(tick, delay);
    };

    tick();
  };

  // ============================================================
  // EDIT TEAM
  // ============================================================

  const updateTeamName = (index, value) => {
    setParsedTeams((current) => {
      const updated = [...current];

      updated[index] = {
        ...updated[index],
        name: value,
      };

      return updated;
    });
  };

  // ============================================================
  // RENDER
  // ============================================================

  return (
    <div className="fs-tv-root">
      {/* POZADINA */}
      <div className="fs-tv-noise" />

      <div className="fs-tv-grid-background" />

      <div className="fs-tv-light fs-tv-light-one" />
      <div className="fs-tv-light fs-tv-light-two" />

      {/* ======================================================
          GORNJA TRAKA S ALATIMA
      ====================================================== */}

      <div className="fs-tv-utility">
        <div className="fs-tv-live-indicator">
          <span className="pulse-dot" />
          <span>UŽIVO</span>
        </div>

        <div className="fs-tv-utility-center">
          FUCASCORE
          <span></span>
          SUSTAV ŽDRIJEBA TURNIRA
        </div>

        <button
          type="button"
          className="fs-tv-fullscreen-button"
          onClick={toggleFullscreen}
        >
          <span className="fullscreen-icon">{isFullscreen ? "⛶" : "⛶"}</span>

          <span>{isFullscreen ? "EXIT FULLSCREEN" : "FULLSCREEN"}</span>
        </button>
      </div>

      {/* ======================================================
          KORAK 1
      ====================================================== */}

      {step === 1 && (
        <div className="fs-tv-page">
          <div className="fs-tv-setup-card">
            <div className="fs-tv-setup-left">
              <div className="fs-tv-kicker">KONTROLA TURNIRA</div>

              <div className="fs-tv-big-logo">
                <img src={FucaScoreLogo} alt="FucaScore" />
              </div>

              <h1>
                TV
                <br />
                <strong>VEČER ŽDRIJEBA</strong>
              </h1>

              <p>Profesionalni ždrijeb turnira pripremljen za veliki ekran.</p>

              <div className="fs-tv-system-status">
                <span />
                SUSTAV SPREMAN
              </div>
            </div>

            <div className="fs-tv-setup-right">
              <div className="fs-tv-section-label">ODABERITE KATEGORIJU</div>

              <div className="fs-tv-division-grid">
                <button
                  type="button"
                  className={`${category === "seniori" ? "selected" : ""} ${completedCategories.seniori ? "disabled-category" : ""}`}
                  onClick={() =>
                    !completedCategories.seniori && setCategory("seniori")
                  }
                  style={
                    completedCategories.seniori
                      ? { opacity: 0.6, cursor: "not-allowed" }
                      : {}
                  }
                >
                  <span className="division-number">01</span>

                  <span className="division-ball">⚽</span>

                  <span className="division-info">
                    <strong>SENIORI</strong>
                    <small>
                      {completedCategories.seniori
                        ? "ŽDRIJEB JE VEĆ IZVUČEN"
                        : "GLAVNI TURNIR"}
                    </small>
                  </span>

                  <span className="division-arrow">
                    {completedCategories.seniori ? "🔒" : "→"}
                  </span>
                </button>

                {areVeteransEnabled && (
                  <button
                    type="button"
                    className={`${category === "veterani" ? "selected" : ""} ${completedCategories.veterani ? "disabled-category" : ""}`}
                    onClick={() =>
                      !completedCategories.veterani && setCategory("veterani")
                    }
                    style={
                      completedCategories.veterani
                        ? { opacity: 0.6, cursor: "not-allowed" }
                        : {}
                    }
                  >
                    <span className="division-number">02</span>

                    <span className="division-ball">🛡️</span>

                    <span className="division-info">
                      <strong>VETERANI</strong>
                      <small>
                        {completedCategories.veterani
                          ? "ŽDRIJEB JE VEĆ IZVUČEN"
                          : "VETERANSKA KATEGORIJA"}
                      </small>
                    </span>

                    <span className="division-arrow">
                      {completedCategories.veterani ? "🔒" : "→"}
                    </span>
                  </button>
                )}
              </div>

              <div className="fs-tv-bottom-actions">
                <button
                  type="button"
                  className="fs-tv-ghost-button"
                  onClick={onClose}
                >
                  ZATVORI
                </button>

                <button
                  type="button"
                  className="fs-tv-main-button"
                  onClick={() => {
                    if (
                      (category === "seniori" && completedCategories.seniori) ||
                      (category === "veterani" && completedCategories.veterani)
                    ) {
                      alert(
                        "Za ovu kategoriju ždrijeb je već uspješno izvučen i spremljen!",
                      );
                      return;
                    }
                    setStep(2);
                  }}
                >
                  NASTAVI
                  <span>→</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ======================================================
          KORAK 2
      ====================================================== */}

      {step === 2 && (
        <div className="fs-tv-page">
          <div className="fs-tv-config">
            <div className="fs-tv-config-header">
              <div>
                <div className="fs-tv-kicker">POSTAVKE ŽDRIJEBA</div>

                <h2>
                  POSTAVKE
                  <span> / EKIPE</span>
                </h2>
              </div>
            </div>

            <div className="fs-tv-config-content">
              <div className="fs-tv-config-groups">
                <div className="fs-tv-section-label">BROJ SKUPINA</div>

                <div className="fs-tv-group-selector">
                  {[2, 4, 6, 8].map((num) => (
                    <button
                      key={num}
                      type="button"
                      className={groupCount === num ? "selected" : ""}
                      onClick={() => setGroupCount(num)}
                    >
                      <strong>{num}</strong>
                      <span>{num === 2 ? "GRUPE" : "GRUPA"}</span>
                    </button>
                  ))}
                </div>

                <div className="fs-tv-format-preview">
                  <div className="format-preview-title">FORMAT</div>

                  <div className="format-preview-main">
                    {groupCount} {groupCount === 2 ? "grupe" : "grupa"}
                  </div>

                  <div className="format-preview-sub">
                    Ekipe će biti ravnomjerno raspoređene.
                  </div>
                </div>
              </div>

              <div className="fs-tv-team-input">
                <div className="fs-tv-section-label">
                  POPIS EKIPA
                  <span>JEDNA EKIPA PO REDKU</span>
                </div>

                <textarea
                  value={rawTeamsText}
                  onChange={(e) => setRawTeamsText(e.target.value)}
                  placeholder={`NK Podsused
Trešnjevka United
Kustošija FC
Gajnice
...`}
                  spellCheck={false}
                />
              </div>
            </div>

            <div className="fs-tv-config-footer">
              <button
                type="button"
                className="fs-tv-ghost-button"
                onClick={() => setStep(1)}
              >
                ← NATRAG
              </button>

              <button
                type="button"
                className="fs-tv-main-button"
                onClick={handleProcessTeamsText}
              >
                VALIDIRAJ EKIPE
                <span>→</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ======================================================
          KORAK 3
      ====================================================== */}

      {step === 3 && (
        <div className="fs-tv-page">
          <div className="fs-tv-review">
            <div className="fs-tv-review-header">
              <div>
                <div className="fs-tv-kicker">ZAVRŠNA PROVJERA</div>

                <h2>
                  SPREMNI
                  <span> ZA ŽDRIJEB</span>
                </h2>
              </div>

              <div className="fs-tv-ready-counter">
                <strong>{parsedTeams.length}</strong>
                <span>EKIPA SPREMNO</span>
              </div>
            </div>

            <div className="fs-tv-review-grid">
              {parsedTeams.map((team, index) => (
                <div key={team.id} className="fs-tv-review-team">
                  <span className="review-index">
                    {(index + 1).toString().padStart(2, "0")}
                  </span>

                  <input
                    type="text"
                    value={team.name}
                    onChange={(e) => updateTeamName(index, e.target.value)}
                  />

                  <span className="review-check">✓</span>
                </div>
              ))}
            </div>

            <div className="fs-tv-review-footer">
              <button
                type="button"
                className="fs-tv-ghost-button"
                onClick={() => setStep(2)}
              >
                ← UREDI
              </button>

              <button
                type="button"
                className="fs-tv-draw-button"
                onClick={startTheDraw}
              >
                <span className="draw-button-pulse" />
                POKRENI ŽDRIJEB UŽIVO
                <strong>↗</strong>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ======================================================
          KORAK 4 — GLAVNI PRIJENOS
      ====================================================== */}

      {step === 4 && (
        <div className="fs-tv-broadcast">
          {/* ZAGLAVLJE */}

          <header className="fs-tv-broadcast-header">
            <div className="fs-tv-broadcast-brand">
              <img src={FucaScoreLogo} alt="FucaScore" />

              <div>
                <strong>FUCASCORE</strong>
                <span>VEČER ŽDRIJEBA</span>
              </div>
            </div>

            <div className="fs-tv-broadcast-progress">
              <div className="progress-topline">
                <span>NAPREDAK ŽDRIJEBA</span>

                <strong>
                  {drawnTeams.toString().padStart(2, "0")}/
                  {totalTeams.toString().padStart(2, "0")}
                </strong>
              </div>

              <div className="progress-bar">
                <div
                  style={{
                    width: `${drawProgress}%`,
                  }}
                />
              </div>
            </div>

            <div className="fs-tv-broadcast-meta">
              <span className="live-dot" />
              UŽIVO
              <strong>{category.toUpperCase()}</strong>
            </div>
          </header>

          {/* GLAVNI SADRŽAJ */}

          <main className="fs-tv-broadcast-main">
            {/* LIJEVA SCENA ŽDRIJEBA */}

            <section className="fs-tv-draw-stage">
              <div className="draw-stage-top">
                <span>SLUČAJNI ŽDRIJEB</span>

                <span>#{drawNumber.toString().padStart(2, "0")}</span>
              </div>

              <div
                className={`fs-tv-draw-machine ${
                  isAnimating ? "is-spinning" : ""
                } ${currentDrawnTeam ? "has-winner" : ""}`}
              >
                <div className="machine-orbit orbit-one" />
                <div className="machine-orbit orbit-two" />
                <div className="machine-orbit orbit-three" />

                <div className="machine-ring">
                  <div className="machine-inner">
                    <div className="machine-label">
                      {isAnimating
                        ? "IZVLAČENJE"
                        : currentDrawnTeam
                          ? "ODABRANO"
                          : "SPREMNO"}
                    </div>

                    <img
                      src={
                        (animationTeam || currentDrawnTeam)?.logo_url ||
                        ShieldImg
                      }
                      alt=""
                    />

                    <div className="machine-team">
                      {animationTeam?.name ||
                        currentDrawnTeam?.name ||
                        "SPREMAN"}
                    </div>

                    <div className="machine-status">
                      {isAnimating
                        ? "BIRANJE EKIPE"
                        : currentDrawnTeam
                          ? "EKIPA IZVUČENA"
                          : "PRITISNITE START"}
                    </div>
                  </div>
                </div>
              </div>

              <div className="fs-tv-draw-number">
                <span>{drawnTeams.toString().padStart(2, "0")}</span>

                <small>IZVUČENIH EKIPA</small>
              </div>

              <button
                type="button"
                className={`fs-tv-draw-trigger ${
                  isAnimating ? "disabled" : ""
                }`}
                onClick={handleDrawNextTeam}
                disabled={isAnimating || drawPool.length === 0}
              >
                <span className="trigger-icon">{isAnimating ? "◌" : "▶"}</span>

                <span>
                  {isAnimating
                    ? "IZVLAČENJE..."
                    : drawPool.length === 0
                      ? "ŽDRIJEB ZAVRŠEN"
                      : "IZVUCI SLJEDEĆU EKIPU"}
                </span>

                {!isAnimating && drawPool.length > 0 && (
                  <small>preostalo {drawPool.length}</small>
                )}
              </button>
              {drawPool.length === 0 && (
                <button
                  className="save-continue-btn"
                  type="button"
                  onClick={handleSaveToDatabase}
                >
                  SPREMI I NASTAVI →
                </button>
              )}
            </section>

            {/* DESNE SKUPINE */}

            <section className={`fs-tv-groups groups-${groupCount}`}>
              {Object.entries(assignedGroups).map(([groupKey, teams]) => (
                <div
                  key={groupKey}
                  className={`fs-tv-group ${
                    activeGroup === groupKey ? "active" : ""
                  }`}
                >
                  <div className="fs-tv-group-header">
                    <div className="group-letter">{groupKey}</div>

                    <div className="group-title">
                      <span>SKUPINA</span>

                      <strong>{groupKey}</strong>
                    </div>

                    <div className="group-count">
                      {teams.length}/{maxGroupSlots}
                    </div>
                  </div>

                  <div
                    className="fs-tv-group-list"
                    style={{
                      gridTemplateRows: `repeat(${maxGroupSlots}, minmax(0, 1fr))`,
                    }}
                  >
                    {Array.from({ length: maxGroupSlots }).map((_, slot) => {
                      const team = teams[slot];

                      return (
                        <div
                          key={slot}
                          className={`fs-tv-group-slot ${
                            team ? "filled" : ""
                          } ${
                            team && lastAssignedTeamId === team.id
                              ? "new-team"
                              : ""
                          }`}
                        >
                          <span className="slot-number">{slot + 1}</span>

                          {team ? (
                            <>
                              <span className="slot-team-name">
                                {team.name}
                              </span>
                            </>
                          ) : (
                            <span className="slot-empty">ČEKANJE EKIPE</span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </section>
          </main>

          {/* DONJA TRAKA */}

          <footer className="fs-tv-broadcast-footer">
            <div>
              <span className="footer-green-dot" />
              SUSTAV ONLINE
            </div>

            <div className="footer-center">
              FUCASCORE
              <span>•</span>
              ŽDRIJEB TURNIRA UŽIVO
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: "15px" }}>
              <span>preostalo ekipa: {drawPool.length}</span>
            </div>
          </footer>
        </div>
      )}

      {/* ======================================================
          KORAK 5
      ====================================================== */}

      {step === 5 && (
        <div className="fs-tv-finished">
          <div className="finished-background-circle" />

          <div className="finished-content">
            <div className="finished-icon">🏆</div>

            <div className="fs-tv-kicker">ŽDRIJEB ZAVRŠEN</div>

            <h2>
              SVE EKIPE SU
              <br />
              <strong>IZVUČENE</strong>
            </h2>

            <div className="finished-stats">
              <div>
                <strong>{totalTeams}</strong>

                <span>EKIPA</span>
              </div>

              <div>
                <strong>{groupCount}</strong>

                <span>SKUPINA</span>
              </div>

              <div>
                <strong>100%</strong>

                <span>ZAVRŠENO</span>
              </div>
            </div>

            <button
              type="button"
              className="fs-tv-finish-button"
              onClick={() => onFinish("teams")}
            >
              ZAVRŠI PRIJENOS
              <span>→</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminTournamentTVBroadcast;
